import { Alert, Button, Card, Form, Image, Input, Select, Tag, message } from "antd";
import type { DeviceCapturePicRecVo, Page } from "@zhihaoscm/entity";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    Pagination as ZhPagination,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { CANOPY_STATE } from "@/meta/logistics/shipInfo";
import { RECOGNIZE_RESULT, RECORD_ADD_TO_MODEL_STATUS } from "@/meta/logistics/monitor";
import { useUserStore } from "@/stores/react/authStore";

export interface IdentifyRecordQuery {
    page: number;
    size: number;
    name?: string;
    state?: number;
    canopyState?: number;
    recResult?: number;
    sTime?: string;
    eTime?: string;
    sortKey?: string;
    sortOrder?: string;
}
export interface IdentifyRecordPageProps {
    loadRecords?: (query: IdentifyRecordQuery) => Promise<Page<DeviceCapturePicRecVo>>;
    addToModel?: (id: number) => Promise<unknown>;
    permissions?: string[];
}
export async function loadIdentifyRecords(query: IdentifyRecordQuery) {
    const response = await shippingRequestHttp.service.get<Page<DeviceCapturePicRecVo>>(
        "/device/capture/pic-rec/paging",
        {
            params: query
        }
    );
    return response.data;
}
export async function addIdentifyRecordToModel(id: number) {
    const response = await shippingRequestHttp.service.post(`/device/capture/pic-rec/${id}/to-dataset`);
    return response.data;
}
const enumText = (meta: { getIntroByCode: (code?: number | string) => string }, value?: number) =>
    meta.getIntroByCode(value) || DEFAULT_VALUE;
const dateText = (value?: string) => (value ? value.replace("T", " ").slice(0, 19) : DEFAULT_VALUE);
/** 对齐 Vue 识别记录结果插槽：装卸状态存在剩余时间时展示预计剩余小时数。 */
const recognitionResultContent = (row: DeviceCapturePicRecVo) => {
    const recognizeResult = row.picRec?.recognizeResult;
    const showRemainingLayTime =
        (recognizeResult === RECOGNIZE_RESULT.LOADING.code || recognizeResult === RECOGNIZE_RESULT.UNLOADING.code) &&
        row.picRec?.remainingLayTime !== null;

    return (
        <>
            <Tag>{enumText(RECOGNIZE_RESULT, recognizeResult)}</Tag>
            {showRemainingLayTime ? <span>预计剩余{row.picRec?.remainingLayTime}h</span> : null}
        </>
    );
};
/** 识别记录列表，图片只展示当前行，加入模型仅由用户点击触发。 */
export function IdentifyRecordPage({
    loadRecords = loadIdentifyRecords,
    addToModel = addIdentifyRecordToModel,
    permissions
}: IdentifyRecordPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    // 保持 Vue hasAuth 语义：识别记录写权限独立控制加入模型入口。
    const canWrite = (permissions ?? storePermissions).includes("ROLE_MODEL_DATA_LOG_W");
    const [rows, setRows] = useState<DeviceCapturePicRecVo[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<IdentifyRecordQuery>({ page: 1, size: 20 });
    const [name, setName] = useState("");
    const [state, setState] = useState<number>();
    const [recResult, setRecResult] = useState<number>();
    // 对齐旧 Vue 识别记录筛选，保存棚架状态查询值。
    const [canopyState, setCanopyState] = useState<number>();
    const [range, setRange] = useState<[string | null, string | null] | null>(null);
    const [error, setError] = useState("");
    useEffect(() => {
        let disposed = false;
        void loadRecords(query)
            .then(result => {
                if (!disposed) {
                    setRows(result.list || []);
                    setTotal(result.total || 0);
                    setError("");
                }
            })
            .catch(() => {
                if (!disposed) {
                    setRows([]);
                    setTotal(0);
                    setError("识别记录加载失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadRecords, query]);
    const submitFilters = () => {
        const next: IdentifyRecordQuery = { page: 1, size: query.size };
        if (name.trim()) next.name = name.trim();
        if (state !== undefined) next.state = state;
        if (recResult !== undefined) next.recResult = recResult;
        if (canopyState !== undefined) next.canopyState = canopyState;
        if (range?.[0]) next.sTime = `${range[0]} 00:00:00`;
        if (range?.[1]) next.eTime = `${range[1]} 23:59:59`;
        setQuery(next);
    };
    const add = useCallback(
        async (row: DeviceCapturePicRecVo) => {
            if (!row.picRec?.id) return;
            try {
                await addToModel(Number(row.picRec.id));
                message.success("已加入到模型数据");
                setQuery(current => ({ ...current }));
            } catch {
                setError("加入模型失败");
            }
        },
        [addToModel]
    );

    // 保留 Vue ProTable 的识别记录创建时间排序参数。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "picRec.createdTime") return;
            setQuery(current => {
                const nextQuery = { ...current, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = "created_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    // 用 zhihao-ui-react 的列契约承接旧 Vue 识别记录 ProTable 字段和操作插槽。
    const columns = useMemo<TableColumnProps<DeviceCapturePicRecVo>[]>(
        () => [
            {
                key: "index",
                label: "序号",
                prop: "index",
                render: ({ rowIndex }) => (query.page - 1) * query.size + rowIndex + 1
            },
            {
                key: "device",
                label: "设备名称",
                prop: "device.name",
                render: ({ row }) => row.device?.name || DEFAULT_VALUE
            },
            {
                key: "image",
                label: "图片",
                prop: "image",
                render: ({ row }) =>
                    row.picFile?.path ? (
                        <Image
                            alt="识别图片"
                            aria-label="识别图片"
                            width={75}
                            src={row.picFile.path}
                            preview={{ src: row.picFile.path }}
                        />
                    ) : (
                        row.picFile?.name || DEFAULT_VALUE
                    )
            },
            {
                key: "result",
                label: "货舱状态识别结果",
                prop: "recognizeResult",
                render: ({ row }) => recognitionResultContent(row)
            },
            {
                key: "canopy",
                label: "棚架状态识别结果",
                prop: "canopyStateResult",
                render: ({ row }) => enumText(CANOPY_STATE, row.picRec?.canopyStateResult)
            },
            {
                key: "match",
                label: "匹配图片",
                prop: "matchImage",
                render: ({ row }) =>
                    row.retrievalPicFile?.path ? (
                        <Image
                            alt="匹配图片"
                            aria-label="匹配图片"
                            width={75}
                            src={row.retrievalPicFile.path}
                            preview={{ src: row.retrievalPicFile.path }}
                        />
                    ) : (
                        row.retrievalPicFile?.name || DEFAULT_VALUE
                    )
            },
            {
                key: "score",
                label: "匹配值",
                prop: "picRec.score",
                render: ({ row }) =>
                    `${row.picRec?.score ?? DEFAULT_VALUE} / ${(row.picRec as { canopyScore?: unknown } | undefined)?.canopyScore ?? DEFAULT_VALUE}`
            },
            {
                key: "time",
                label: "创建时间",
                prop: "picRec.createdTime",
                sortable: true,
                render: ({ row }) => dateText(row.picRec?.createdTime)
            },
            {
                key: "state",
                label: "状态",
                prop: "picRec.status",
                render: ({ row }) => enumText(RECORD_ADD_TO_MODEL_STATUS, row.picRec?.status)
            },
            {
                key: "operation",
                label: "操作",
                prop: "operation",
                fixed: "right",
                render: ({ row }) =>
                    canWrite && Number(row.picRec?.status) === RECORD_ADD_TO_MODEL_STATUS.UNLABELED.code ? (
                        <Button type="link" disabled={banButton} onClick={() => void add(row)}>
                            加入模型
                        </Button>
                    ) : null
            },
            {
                key: "detail",
                label: "详情",
                prop: "detail",
                render: ({ row }) => <Link to={`/logistics/monitor/identifyRecord/${row.picRec?.id}`}>查看</Link>
            }
        ],
        [add, banButton, canWrite, query.page, query.size]
    );
    return (
        <section aria-label="identify-record-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Form layout="inline" onFinish={submitFilters}>
                    <Form.Item label="设备名称">
                        <Input aria-label="设备名称" value={name} onChange={event => setName(event.target.value)} />
                    </Form.Item>
                    <Form.Item label="识别结果">
                        <Select
                            aria-label="识别结果"
                            allowClear
                            value={recResult}
                            options={RECOGNIZE_RESULT.options.map(item => ({
                                label: item.label,
                                value: Number(item.value)
                            }))}
                            onChange={setRecResult}
                        />
                    </Form.Item>
                    <Form.Item label="棚架状态识别结果">
                        <Select
                            aria-label="棚架状态识别结果"
                            allowClear
                            value={canopyState}
                            options={CANOPY_STATE.options.map(item => ({
                                label: item.label,
                                value: Number(item.value)
                            }))}
                            onChange={setCanopyState}
                        />
                    </Form.Item>
                    <Form.Item label="状态">
                        <Select
                            aria-label="状态"
                            allowClear
                            value={state}
                            options={RECORD_ADD_TO_MODEL_STATUS.options.map(item => ({
                                label: item.label,
                                value: Number(item.value)
                            }))}
                            onChange={setState}
                        />
                    </Form.Item>
                    <Form.Item label="创建时间">
                        <ZhDateRangePicker
                            aria-label="创建时间"
                            endPlaceholder="创建结束时间"
                            format="YYYY-MM-DD"
                            startPlaceholder="创建开始时间"
                            type="daterange"
                            value={range}
                            valueFormat="YYYY-MM-DD"
                            onChange={(_value, formattedValue) => setRange(formattedValue)}
                        />
                    </Form.Item>
                    <Button htmlType="submit" type="primary">
                        查询
                    </Button>
                </Form>
            </Card>
            <Card title="识别记录">
                <ZhTable<DeviceCapturePicRecVo>
                    rowKey={row => String(row.picRec?.id)}
                    data={rows}
                    columns={columns}
                    emptyText="暂无数据"
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            {error ? <Alert role="alert" message={error} type="error" /> : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                onChange={page => setQuery(current => ({ ...current, page }))}
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}
